import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Button,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { useHangarsState } from "../../containers/HangarsStateContainer";
import { useRampsState } from "../../containers/RampsStateContainer";
import { usePermissions } from "../../hooks/usePermissions";
import { sortHangars } from "../../screens/Hangars/HangarsPresenter";
import { sortRamps } from "../../screens/Ramps/RampsPresenter";
import { PlacementService } from "../../services/PlacementService";
import { Hangar, Preference, Ramp, Stack as StackType, Tenant, TripWithTenant } from "../../types";
import { ButtonNotes } from "../ButtonNotes/ButtonNotes";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { EditTenant } from "../EditTenant";
import { LocationsContainer } from "../../containers/LocationsStateContainer";
import { TripDetailsModal } from "../../screens/Schedule/TripDetailsModal/TripDetailsModal";
import { TripService } from "../../services/TripService";

// StackOptionButton component for preference options
const StackOptionButton: React.FC<{
  text: string;
  selected: boolean;
  onClick: () => void;
}> = ({ text, selected, onClick }) => {
  return (
    <MenuItem onClick={onClick} data-testid={`preferences_menu_${text}`}>
      {selected && (
        <ListItemIcon>
          <CheckIcon />
        </ListItemIcon>
      )}
      <ListItemText inset={!selected}>
        <Typography variant="button">{text}</Typography>
      </ListItemText>
    </MenuItem>
  );
};

interface Props {
  tenant: Tenant;
  tripId: string;
  currentTimestamp: Date;
  stack: StackType;
  setStack: (stack: StackType) => void;
  onClose: () => void;
  onRefresh: () => void;
  onRemoveAircraft?: (tenantId: string, tripId: string) => void;
  menuProps?: any;
}

export const ScheduledAircraftContextMenu: React.FC<Props> = ({
  tenant,
  tripId,
  currentTimestamp,
  stack,
  setStack,
  onClose,
  onRefresh,
  onRemoveAircraft,
  menuProps = {},
}) => {
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const { isViewOnlyMode } = usePermissions();
  const { hangars: allHangars } = useHangarsState();
  const { ramps } = useRampsState();
  
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [moveToAnchor, setMoveToAnchor] = React.useState<null | HTMLElement>(null);
  const moveToOpen = Boolean(moveToAnchor);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [viewTripsOpen, setViewTripsOpen] = React.useState(false);
  const [aircraftTrips, setAircraftTrips] = React.useState<TripWithTenant[]>([]);
  const [selectedTrip, setSelectedTrip] = React.useState<TripWithTenant | null>(null);
  const [tripsLoading, setTripsLoading] = React.useState(false);
  
  // Extract current preferences from tenant
  const [preferences, setPreferences] = React.useState<Preference[]>(
    tenant.position?.preferences || []
  );
  
  // Get sorted locations
  const locations: Array<Ramp | Hangar> = React.useMemo(() => [
    ...allHangars.sort((a, b) =>
      sortHangars(a, b, activeFBO?.settings?.hangarDisplayOrder)
    ),
    ...ramps.sort((a, b) =>
      sortRamps(a, b, activeFBO?.settings?.rampDisplayOrder)
    ),
  ], [allHangars, ramps, activeFBO]);
  
  // Determine current location
  const currentLocationId = React.useMemo(() => {
    // Try to find the current location from the stack
    const hangar = allHangars.find(h => h.id === stack.location_id);
    if (hangar) return hangar.id;
    
    const ramp = ramps.find(r => r.id === stack.location_id);
    if (ramp) return ramp.id;
    
    return null;
  }, [stack, allHangars, ramps]);
  
  // Check if in hangar
  const isHangar = allHangars.some(h => h.id === stack.location_id);
  
  // Preference state helpers
  const isNoseIn =
    preferences.indexOf(Preference.NOSE_IN) > -1 ||
    preferences.indexOf(Preference.TAIL_WHEEL_NOSE_IN) > -1;
  const isTailIn =
    preferences.indexOf(Preference.TAIL_IN) > -1 ||
    preferences.indexOf(Preference.TAIL_WHEEL_TAIL_IN) > -1;

  const handleLocationChange = async (
    locationId: string | null,
    locationType: "hangar" | "ramp" | null,
    position?: { x: number | null; y: number | null; angle: number | null }
  ) => {
    // Clear any previous errors
    setError(null);
    
    // Validate required data
    if (!tripId || !postgrest || !currentTimestamp) {
      setError("Missing required data for location change");
      return;
    }

    setIsUpdating(true);
    try {
      const placementService = new PlacementService(postgrest);
      const startTimeISO = currentTimestamp.toISOString();
      
      // Determine if we should clear position
      // Clear position when: moving to unassigned OR moving to a different location
      const shouldClearPosition = !locationId || locationId !== currentLocationId;
      const finalPosition = shouldClearPosition 
        ? { x: null, y: null, angle: null }
        : position || { x: tenant.position?.x || null, y: tenant.position?.y || null, angle: tenant.position?.angle || null };
      
      // Use the stored procedure with gap filling
      // excludeConflicts is TRUE to allow scheduling flexibility - users can manage conflicts
      await placementService.assignToLocationWithGapFilling(
        tripId,
        locationId,
        locationType,
        startTimeISO,  // Only affect from current time forward
        undefined,     // Let it go to trip end
        true,          // Allow moves even with conflicts - scheduling needs flexibility
        finalPosition  // Pass the position explicitly
      );

      // Success! Trigger refresh to reload data
      onRefresh();
      
      // Close the menus
      setMoveToAnchor(null);
      onClose();
    } catch (error: any) {
      console.error("Failed to update location:", error);
      
      // Show user-friendly error message
      if (error.message?.includes('conflict')) {
        setError("Another aircraft is already in this location at this time");
      } else if (error.message?.includes('gap')) {
        setError("Cannot create gaps in the schedule");
      } else {
        setError(error.message || "Failed to update location");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMoveToClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoveToAnchor(event.currentTarget);
  };

  const handleMoveToClose = () => {
    setMoveToAnchor(null);
  };

  if (isViewOnlyMode()) {
    return (
      <MenuList dense={false} {...menuProps}>
        <Stack direction="column">
          <ButtonNotes
            disabled={true}
            tenant={tenant}
            setTenant={() => {}}
          />
        </Stack>
      </MenuList>
    );
  }

  return (
    <>
      {confirmDelete && (
        <ConfirmationDialog
          title="Please Confirm"
          text="This will remove the aircraft from the schedule at this time."
          okText="Yes, remove from schedule"
          onOk={() => {
            if (onRemoveAircraft) {
              onRemoveAircraft(tenant.id, tripId);
            } else {
              handleLocationChange(null, null);
            }
            setConfirmDelete(false);
          }}
          onClose={() => setConfirmDelete(false)}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      
      <MenuList dense={false} {...menuProps}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
            {error}
          </Alert>
        )}
        
        <Stack direction="column">
          {/* Notes button */}
          <ButtonNotes
            tenant={tenant}
            setTenant={(updatedTenant) => {
              setStack({
                ...stack,
                tenants: stack.tenants.map((t) =>
                  t.id === tenant.id ? updatedTenant : t
                ),
              });
            }}
          />

          {/* View Trips */}
          <Button
            size="small"
            color="inherit"
            variant="text"
            onClick={async () => {
              if (!activeFBO?.id || !tenant.tail_number) return;

              // Close any open submenus
              setMoveToAnchor(null);

              setTripsLoading(true);
              try {
                const tripService = new TripService(postgrest);
                const trips = await tripService.getTripsByTailNumber(
                  activeFBO.id,
                  tenant.tail_number
                );
                setAircraftTrips(trips);

                // Find the current trip or the most recent one
                const currentTrip = trips.find(t => t.trip_id === tripId) ||
                                  trips.find(t => t.status === 'active') ||
                                  trips[trips.length - 1];

                if (currentTrip) {
                  setSelectedTrip(currentTrip);
                  setViewTripsOpen(true);
                }
              } catch (error) {
                console.error('Failed to fetch trips:', error);
                setError('Failed to load trips for this aircraft');
              } finally {
                setTripsLoading(false);
              }
            }}
            disabled={tripsLoading}
          >
            {tripsLoading ? <CircularProgress size={16} /> : "View Trips"}
          </Button>

          {/* Lock/Unlock toggle */}
          <Button
            size="small"
            color="inherit"
            variant="text"
            onClick={async () => {
              if (!postgrest || !tenant.placement_id) return;

              const newLockState = !tenant.is_locked;
              const placementService = new PlacementService(postgrest);

              try {
                await placementService.toggleLock(tenant.placement_id, newLockState);

                // Update local state
                setStack({
                  ...stack,
                  tenants: stack.tenants.map((t) =>
                    t.id === tenant.id
                      ? { ...t, is_locked: newLockState }
                      : t
                  ),
                });

                onClose();
                onRefresh();
              } catch (error) {
                console.error('Failed to toggle lock:', error);
                setError('Failed to toggle lock status');
              }
            }}
          >
            {tenant.is_locked ? "🔓 Unlock" : "🔒 Lock"}
          </Button>

          <Divider />
        </Stack>
        
        {/* Preference options - only show in hangar */}
        {isHangar && (
          <div>
            <StackOptionButton
              selected={isTailIn}
              text="Tail In"
              onClick={() => {
                const newPrefs = isTailIn
                  ? preferences.filter(
                      (p) =>
                        p !== Preference.TAIL_IN &&
                        p !== Preference.TAIL_WHEEL_TAIL_IN
                    )
                  : [
                      ...preferences.filter(
                        (p) =>
                          p !== Preference.NOSE_IN &&
                          p !== Preference.TAIL_WHEEL_NOSE_IN
                      ),
                      tenant.aircraft?.tail_wheel
                        ? Preference.TAIL_WHEEL_TAIL_IN
                        : Preference.TAIL_IN,
                    ];
                setPreferences(newPrefs);
                // TODO: Update preferences in database
              }}
            />
            <StackOptionButton
              selected={isNoseIn}
              text="Nose In"
              onClick={() => {
                const newPrefs = isNoseIn
                  ? preferences.filter(
                      (p) =>
                        p !== Preference.NOSE_IN &&
                        p !== Preference.TAIL_WHEEL_NOSE_IN
                    )
                  : [
                      ...preferences.filter(
                        (p) =>
                          p !== Preference.TAIL_IN &&
                          p !== Preference.TAIL_WHEEL_TAIL_IN
                      ),
                      tenant.aircraft?.tail_wheel
                        ? Preference.TAIL_WHEEL_NOSE_IN
                        : Preference.NOSE_IN,
                    ];
                setPreferences(newPrefs);
                // TODO: Update preferences in database
              }}
            />
            <StackOptionButton
              selected={preferences.indexOf(Preference.PREFER_FRONT_ROW) > -1}
              text="Front Row"
              onClick={() => {
                const newPrefs =
                  preferences.indexOf(Preference.PREFER_FRONT_ROW) > -1
                    ? preferences.filter((p) => p !== Preference.PREFER_FRONT_ROW)
                    : [
                        ...preferences.filter((p) => p !== Preference.PREFER_BACK_ROW),
                        Preference.PREFER_FRONT_ROW,
                      ];
                setPreferences(newPrefs);
                // TODO: Update preferences in database
              }}
            />
            <StackOptionButton
              selected={preferences.indexOf(Preference.PREFER_BACK_ROW) > -1}
              text="Back Row"
              onClick={() => {
                const newPrefs =
                  preferences.indexOf(Preference.PREFER_BACK_ROW) > -1
                    ? preferences.filter((p) => p !== Preference.PREFER_BACK_ROW)
                    : [
                        ...preferences.filter((p) => p !== Preference.PREFER_FRONT_ROW),
                        Preference.PREFER_BACK_ROW,
                      ];
                setPreferences(newPrefs);
                // TODO: Update preferences in database
              }}
            />
            <StackOptionButton
              selected={preferences.indexOf(Preference.OK_TO_LEAVE_OUT) > -1}
              text="OK Outside"
              onClick={() => {
                const newPrefs =
                  preferences.indexOf(Preference.OK_TO_LEAVE_OUT) > -1
                    ? preferences.filter((p) => p !== Preference.OK_TO_LEAVE_OUT)
                    : [...preferences, Preference.OK_TO_LEAVE_OUT];
                setPreferences(newPrefs);
                // TODO: Update preferences in database
              }}
            />
            <StackOptionButton
              selected={preferences.indexOf(Preference.LOCKED) > -1}
              text="Locked"
              onClick={() => {
                const newPrefs =
                  preferences.indexOf(Preference.LOCKED) > -1
                    ? preferences.filter((p) => p !== Preference.LOCKED)
                    : [...preferences, Preference.LOCKED];
                setPreferences(newPrefs);
                // TODO: Update preferences in database
              }}
            />
            <Divider />
          </div>
        )}
        
        <Stack direction="column">
          {/* Move To button with submenu */}
          <Button
            fullWidth
            color="primary"
            endIcon={moveToOpen ? null : <ChevronRightIcon />}
            onClick={handleMoveToClick}
            disabled={isUpdating}
          >
            {isUpdating ? <CircularProgress size={20} /> : "Move To"}
          </Button>

          {/* Edit Aircraft - now enabled */}
          <Button 
            fullWidth 
            color="primary" 
            onClick={() => setOpenEdit(true)}
          >
            Edit Aircraft
          </Button>
          
          {/* Remove from Schedule */}
          <Button 
            fullWidth 
            color="error"
            onClick={() => setConfirmDelete(true)}
            disabled={isUpdating}
          >
            Remove Aircraft
          </Button>
        </Stack>
      </MenuList>

      {/* Move To Submenu */}
      <Menu
        anchorEl={moveToAnchor}
        open={moveToOpen}
        onClose={handleMoveToClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {locations.map((location) => {
          const isCurrentLocation = location.id === currentLocationId;
          const locationType = "stacks" in location ? "hangar" : "ramp";
          
          return (
            <MenuItem
              key={`moveto-location-${location.id}`}
              disabled={isUpdating}
              onClick={() => {
                handleLocationChange(location.id, locationType);
                handleMoveToClose();
              }}
            >
              {isCurrentLocation && (
                <ListItemIcon>
                  <CheckIcon />
                </ListItemIcon>
              )}
              <ListItemText inset={!isCurrentLocation}>
                <Typography variant="button">
                  {location.name}
                </Typography>
              </ListItemText>
            </MenuItem>
          );
        })}
        
        <Divider />
        
        {/* Unassigned option */}
        <MenuItem
          disabled={isUpdating}
          onClick={() => {
            handleLocationChange(null, null);
            handleMoveToClose();
          }}
        >
          <ListItemText>
            <Typography variant="button">
              Unassigned
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Trip Details Modal */}
      <TripDetailsModal
        open={viewTripsOpen}
        trip={selectedTrip}
        trips={aircraftTrips}
        onClose={() => {
          setViewTripsOpen(false);
          setSelectedTrip(null);
        }}
        onNavigate={(newTripId) => {
          const newTrip = aircraftTrips.find(t => t.trip_id === newTripId);
          if (newTrip) {
            setSelectedTrip(newTrip);
          }
        }}
        onTripChanged={async () => {
          // Refresh trips after changes
          if (!activeFBO?.id || !tenant.tail_number) return;

          try {
            const tripService = new TripService(postgrest);
            const trips = await tripService.getTripsByTailNumber(
              activeFBO.id,
              tenant.tail_number
            );
            setAircraftTrips(trips);

            // Update selected trip if it still exists
            if (selectedTrip) {
              const updatedTrip = trips.find(t => t.trip_id === selectedTrip.trip_id);
              if (updatedTrip) {
                setSelectedTrip(updatedTrip);
              }
            }
          } catch (error) {
            console.error('Failed to refresh trips:', error);
          }

          onRefresh();
        }}
        currentTimestamp={currentTimestamp}
      />
      
      {/* Edit Tenant Dialog */}
      {openEdit && (
        <LocationsContainer>
          <EditTenant
            displayTypeAndLocation={false}
            open={openEdit}
            onClose={() => setOpenEdit(false)}
            tenant={tenant}
            setTenant={(updatedTenant) => {
              setStack({
                ...stack,
                tenants: stack.tenants.map((t) =>
                  t.id === tenant.id ? updatedTenant : t
                ),
              });
              setOpenEdit(false);
              onRefresh(); // Refresh the view after editing
            }}
          />
        </LocationsContainer>
      )}
    </>
  );
};